/**
 * Flattens a RateShop response into the shape the table renders, and works out
 * what the numbers actually mean.
 *
 * The interesting judgement is here rather than in the UI: which quote is
 * cheapest among those that still hit the deadline, which is genuinely fastest,
 * and which are *dominated* - slower AND more expensive than another option on
 * the same screen, so there is never a reason to pick them. The reference lane
 * has exactly that shape: Ground and Priority Overnight deliver on the same day,
 * so the $24 premium buys only a time-of-day commitment, and 2Day is beaten on
 * both axes at once.
 */

const CHARGE_LABELS = {
  'BASE CHG': 'Base charge',
  'DISCOUNT CHG': 'Negotiated discount',
  'EXT AREA CHG': 'Extended area',
  'OVERSIZE CHG': 'Oversize',
  'OVERLENGTH CHG': 'Overlength',
  'RESIDENTIAL CHG': 'Residential',
  'FUEL CHG': 'Fuel surcharge',
  'SATURDAY CHG': 'Saturday delivery',
  'DAS CHG': 'Delivery area',
};

/** "09/01/2026" -> "2026-09-01". Returns null for anything unparseable. */
function usDateToIso(value) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(value ?? '').trim());
  if (!m) return null;
  return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
}

/** Turns "5:00 PM" into minutes past midnight, for ranking commit times. */
function commitMinutes(value) {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(value ?? '').trim());
  if (!m) return 24 * 60;
  let hours = Number(m[1]) % 12;
  if (/PM/i.test(m[3])) hours += 12;
  return hours * 60 + Number(m[2]);
}

function chargeMap(charges) {
  const out = new Map();
  for (const c of charges ?? []) out.set(c.ID, Number(c.Value) || 0);
  return out;
}

function attrMap(attributes) {
  const out = new Map();
  for (const a of attributes ?? []) out.set(a.ID, a.Value);
  return out;
}

function money(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function normaliseQuote(result) {
  const shipment = result.Shipment ?? {};
  const summary = shipment.Summary ?? {};
  const pkg = (shipment.Packages ?? [])[0] ?? {};
  const via = shipment.ShipVia ?? {};

  const charges = chargeMap(result.Charges);
  const listCharges = chargeMap(pkg.ListCharges);
  const attrs = attrMap(pkg.Attributes);

  const rate = money(result.Rate ?? summary.TotalCharge);
  const listTotal = money(listCharges.get('LST TOTAL CHG'));
  const baseCharge = money(summary.BaseCharge ?? charges.get('BASE CHG'));
  const discount = money(summary.Discount ?? charges.get('DISCOUNT CHG'));

  const savings = listTotal > 0 ? money(listTotal - rate) : null;

  return {
    shipVia: result.ShipVia ?? via.ShipVia ?? '',
    serviceName: result.ServiceDescription ?? via.ServiceName ?? via.Description ?? result.ShipVia,
    carrier: via.CarrierName ?? '',
    scac: via.SCAC ?? result.SCAC ?? '',
    carrierType: result.CarrierType ?? via.CarrierType ?? '',
    rateEngine: result.RateEngine ?? '',

    rate,
    listTotal: listTotal || null,
    savings,
    savingsPct: listTotal > 0 ? Math.round((1 - rate / listTotal) * 1000) / 10 : null,

    baseCharge,
    discount,
    netBase: money(baseCharge + discount),
    discountPct: baseCharge > 0 ? Math.round((-discount / baseCharge) * 1000) / 10 : null,
    ssCharge: money(summary.SSCharge),
    fuelCharge: money(summary.FuelCharge),

    transitDays: Number(result.ActTNTDays ?? result.TNTDays ?? 0),
    deliveryDate: usDateToIso(result.ActDeliveryDate),
    deliveryDateRaw: result.ActDeliveryDate ?? '',
    commitTime: result.TNTTime ?? summary.TNTTime ?? '',
    commitMinutes: commitMinutes(result.TNTTime ?? summary.TNTTime),
    zone: String(result.Zone ?? attrs.get('Zone') ?? '').replace(/^0+(?=\d)/, ''),

    actualWeight: money(summary.ActualWeight ?? pkg.ActualWeight),
    billedWeight: money(summary.BilledWeight ?? pkg.BilledWeight),
    dimApplied: attrs.get('DIM FLAG') === 'Y',
    dimWeight: attrs.get('DIMWeight') ? money(attrs.get('DIMWeight')) : null,
    discountType: attrs.get('RateDiscountType') ?? '',

    charges: (result.Charges ?? []).map((c) => ({
      id: c.ID,
      label: CHARGE_LABELS[c.ID] ?? c.ID,
      value: money(c.Value),
    })),

    warning: shipment.WarningMsg?.trim() || null,
    errorNum: result.ErrorNum ?? 0,
    errorDesc: result.ErrorDesc ?? '',
  };
}

/**
 * @param {object} response  Raw RateShop response envelope.
 * @param {object} load      The load the shop was run for.
 */
export function normaliseRateShop(response, load) {
  const results = response?.ResultData?.Results ?? [];

  const quotes = results
    .filter((r) => (r.ErrorNum ?? 0) === 0 && Number(r.Rate) > 0)
    .map(normaliseQuote);

  const failures = results
    .filter((r) => (r.ErrorNum ?? 0) !== 0)
    .map((r) => ({
      shipVia: r.ShipVia,
      serviceName: r.ServiceDescription ?? r.ShipVia,
      errorNum: r.ErrorNum,
      errorDesc: r.ErrorDesc,
    }));

  // --- Deadline -------------------------------------------------------------
  const requiredBy = load.requiredByDate;
  for (const q of quotes) {
    // Absolute arrival instant, not date and commit time as separate axes.
    // Ground delivering 9/1 by 11:59 PM genuinely beats 2Day arriving 9/2 by
    // 5:00 PM, and only a combined timestamp says so.
    q.arrivesAt = q.deliveryDate
      ? new Date(`${q.deliveryDate}T00:00:00Z`).getTime() + q.commitMinutes * 60000
      : null;
    q.meetsDeadline = q.deliveryDate && requiredBy ? q.deliveryDate <= requiredBy : true;
    q.daysEarly =
      q.deliveryDate && requiredBy
        ? Math.round((new Date(requiredBy) - new Date(q.deliveryDate)) / 86400000)
        : null;
  }

  const eligible = quotes.filter((q) => q.meetsDeadline);
  const pool = eligible.length ? eligible : quotes;

  // --- Superlatives ---------------------------------------------------------
  const cheapest = pool.reduce((best, q) => (!best || q.rate < best.rate ? q : best), null);

  // Fastest means the earliest the freight is actually on the dock.
  const fastest = pool.reduce((best, q) => {
    if (!best) return q;
    if (q.arrivesAt === null) return best;
    if (best.arrivesAt === null) return q;
    return q.arrivesAt < best.arrivesAt ? q : best;
  }, null);

  /**
   * Dominated: some other quote costs no more and arrives no later, and beats it
   * on at least one. Picking a dominated service is never rational, which is the
   * whole argument for rate shopping rather than defaulting to a service code on
   * the customer master.
   */
  for (const q of quotes) {
    const dominator = quotes.find((other) => {
      if (other === q) return false;
      if (other.arrivesAt === null || q.arrivesAt === null) return false;
      const noWorse = other.rate <= q.rate && other.arrivesAt <= q.arrivesAt;
      const strictlyBetter = other.rate < q.rate || other.arrivesAt < q.arrivesAt;
      return noWorse && strictlyBetter;
    });
    q.dominatedBy = dominator ? dominator.shipVia : null;
  }

  for (const q of quotes) {
    q.badges = [
      q === cheapest ? 'cheapest' : null,
      q === fastest && fastest !== cheapest ? 'fastest' : null,
      q === cheapest && q === fastest ? 'fastest' : null,
      !q.meetsDeadline ? 'misses-deadline' : null,
      q.dominatedBy ? 'dominated' : null,
    ].filter(Boolean);
  }

  quotes.sort((a, b) => a.rate - b.rate);

  // --- The headline ---------------------------------------------------------
  const recommended = cheapest ?? null;
  const premiumOverCheapest = fastest && cheapest ? money(fastest.rate - cheapest.rate) : null;

  // When the deadline knocks out the cheapest service outright, the gap between
  // it and the cheapest option that still makes the date is the number worth
  // saying out loud - that difference is what the promised date actually costs.
  const cheapestOverall = quotes.reduce((best, q) => (!best || q.rate < best.rate ? q : best), null);
  const deadlinePremium =
    cheapest && cheapestOverall && cheapest !== cheapestOverall
      ? money(cheapest.rate - cheapestOverall.rate)
      : null;
  const sameDayAsFastest =
    cheapest && fastest && cheapest.deliveryDate && cheapest.deliveryDate === fastest.deliveryDate;

  const spread =
    quotes.length > 1
      ? money(Math.max(...quotes.map((q) => q.rate)) - Math.min(...quotes.map((q) => q.rate)))
      : 0;

  const carrierWarnings = [
    ...new Set(quotes.map((q) => q.warning).filter(Boolean)),
  ];

  return {
    loadId: load.loadId,
    requiredByDate: requiredBy,
    shipDate: load.shipDate,
    quoteCount: quotes.length,
    quotes,
    failures,
    recommendedShipVia: recommended?.shipVia ?? null,
    analysis: {
      cheapestShipVia: cheapest?.shipVia ?? null,
      fastestShipVia: fastest?.shipVia ?? null,
      cheapestOverallShipVia: cheapestOverall?.shipVia ?? null,
      deadlinePremium,
      spread,
      dominatedCount: quotes.filter((q) => q.dominatedBy).length,
      missesDeadlineCount: quotes.filter((q) => !q.meetsDeadline).length,
      /** True when the cheapest option arrives on the same day as the fastest. */
      cheapestTiesFastestOnDate: Boolean(sameDayAsFastest),
      premiumOverCheapest,
    },
    carrierWarnings,
  };
}
